/*
 * Shared article layout for the HussarAi /wiedza knowledge section.
 *
 * Provides bilingual rendering (PL default, EN via language switcher),
 * consistent typography, meta / canonical / OpenGraph / Article JSON-LD
 * injection, and internal-link footer. Zero backend calls, static.
 *
 * Doctrine preserved verbatim on every article: "AI prepares. Humans decide."
 * / "AI przygotowuje. Ludzie decydują."
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import HussarTopNav from './HussarTopNav';

const AUTHOR_NAME = 'HussarAi';
const PUBLISHER_URL = 'https://hussarai.org';

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    const isMeta = selector.startsWith('meta');
    el = document.createElement(isMeta ? 'meta' : 'link');
    if (selector.includes('property=')) {
      const prop = selector.match(/property="([^"]+)"/)?.[1];
      if (prop) el.setAttribute('property', prop);
    } else if (selector.includes('name=')) {
      const name = selector.match(/name="([^"]+)"/)?.[1];
      if (name) el.setAttribute('name', name);
    } else if (selector.includes('rel=')) {
      const rel = selector.match(/rel="([^"]+)"/)?.[1];
      if (rel) el.setAttribute('rel', rel);
    }
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setJsonLd = (id, payload) => {
  let el = document.head.querySelector(`script[data-jsonld-id="${id}"]`);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-jsonld-id', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
};

const styles = {
  main: {
    minHeight: '100vh',
    background: 'var(--hussar-bg-page, #0B1220)',
    color: 'var(--hussar-text-primary, #E5E7EB)',
    padding: '56px 20px 96px',
  },
  container: { maxWidth: 760, margin: '0 auto' },
  backLink: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontSize: 13, color: 'rgba(125,211,252,0.85)',
    textDecoration: 'none', marginBottom: 28,
  },
  eyebrow: {
    fontSize: 12,
    letterSpacing: 2,
    textTransform: 'uppercase',
    color: 'rgba(125,211,252,0.7)',
    margin: '0 0 12px',
  },
  h1: {
    fontSize: 32, lineHeight: 1.15, margin: '0 0 12px',
    color: '#F1F5F9', fontWeight: 600,
  },
  meta: { fontSize: 13, color: '#94A3B8', marginBottom: 32 },
  lede: { fontSize: 17, color: '#CBD5F5', marginBottom: 28, lineHeight: 1.55 },
  h2: {
    fontSize: 20, margin: '32px 0 12px', color: '#F1F5F9', fontWeight: 600,
  },
  p: { fontSize: 16, lineHeight: 1.7, color: '#CBD5F5', margin: '0 0 16px' },
  callout: {
    background: 'rgba(148,163,184,0.08)',
    borderLeft: '3px solid #94A3B8',
    padding: '14px 16px',
    borderRadius: 6,
    marginBottom: 20,
    fontSize: 15,
    color: '#CBD5F5',
  },
  doctrine: {
    marginTop: 40,
    paddingTop: 24,
    borderTop: '1px solid rgba(148,163,184,0.2)',
    fontStyle: 'italic',
    fontSize: 14,
    color: '#94A3B8',
  },
  linksRow: {
    display: 'flex', flexWrap: 'wrap', gap: 12,
    marginTop: 24, paddingTop: 24,
    borderTop: '1px solid rgba(148,163,184,0.15)',
  },
  linkChip: {
    fontSize: 13, color: '#7DD3FC',
    textDecoration: 'none',
    padding: '6px 12px',
    border: '1px solid rgba(125,211,252,0.3)',
    borderRadius: 999,
  },
};

const WiedzaArticleLayout = ({
  slugId,
  canonicalUrl,
  publishedDate,
  meta: { titlePl, titleEn, descriptionPl, descriptionEn },
  content: { eyebrowPl, eyebrowEn, ledePl, ledeEn, bodyPl, bodyEn },
  internalLinks,
}) => {
  const { i18n } = useTranslation();
  const isPl = (i18n?.language || 'pl').toLowerCase().startsWith('pl');
  const title = isPl ? titlePl : titleEn;
  const description = isPl ? descriptionPl : descriptionEn;

  useEffect(() => {
    document.title = `${title} | HussarAi`;
    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:type"]', 'content', 'article');
    setMeta('meta[property="og:url"]', 'content', canonicalUrl);
    setMeta('meta[property="og:site_name"]', 'content', 'HussarAi');
    setMeta('meta[property="article:author"]', 'content', AUTHOR_NAME);
    setMeta('meta[property="article:published_time"]', 'content', publishedDate);
    setMeta('link[rel="canonical"]', 'href', canonicalUrl);
    setJsonLd(slugId, {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description,
      inLanguage: isPl ? 'pl-PL' : 'en',
      datePublished: publishedDate,
      dateModified: publishedDate,
      author: {
        '@type': 'Person',
        '@id': 'https://hussarai.org/konrad-kwiatkowski#person',
        name: 'Konrad Kwiatkowski',
        url: 'https://hussarai.org/konrad-kwiatkowski',
        image: {
          '@type': 'ImageObject',
          url: 'https://hussarai.org/images/konrad-kwiatkowski-founder-hussarai.webp',
          contentUrl: 'https://hussarai.org/images/konrad-kwiatkowski-founder-hussarai.webp',
          caption: 'Konrad Kwiatkowski, Founder of HussarAi',
          width: 1152,
          height: 1536,
        },
      },
      publisher: {
        '@type': 'Organization',
        '@id': 'https://hussarai.org/#org',
        name: AUTHOR_NAME,
        url: PUBLISHER_URL,
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    });
  }, [title, description, canonicalUrl, publishedDate, slugId, isPl]);

  const publishedLabel = isPl
    ? `Opublikowano ${publishedDate} · Autor: `
    : `Published ${publishedDate} · Author: `;

  return (
    <main data-testid={`wiedza-article-${slugId}`} style={styles.main}>
      <HussarTopNav />
      <div style={styles.container}>
        <Link to="/wiedza" style={styles.backLink} data-testid="wiedza-article-back">
          <ArrowLeft className="w-4 h-4" />{' '}
          {isPl ? 'Powrót do Wiedzy' : 'Back to Knowledge'}
        </Link>
        <p style={styles.eyebrow} data-testid="wiedza-article-eyebrow">
          {isPl ? eyebrowPl : eyebrowEn}
        </p>
        <h1 style={styles.h1} data-testid="wiedza-article-title">{title}</h1>
        <p style={styles.meta} data-testid="wiedza-article-meta">
          {publishedLabel}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, verticalAlign: 'middle' }}>
            <picture>
              <source
                srcSet="/images/konrad-kwiatkowski-founder-hussarai.webp"
                type="image/webp"
              />
              <img
                src="/images/konrad-kwiatkowski-founder-hussarai.jpg"
                alt="Konrad Kwiatkowski, founder of HussarAi"
                width={1152}
                height={1536}
                loading="lazy"
                data-testid="wiedza-article-author-avatar"
                style={{
                  display: 'block', width: 24, height: 24,
                  borderRadius: '50%', objectFit: 'cover',
                  border: '1px solid rgba(148,163,184,0.2)',
                }}
              />
            </picture>
            <Link
              to="/konrad-kwiatkowski"
              data-testid="wiedza-article-author-link"
              style={{ color: 'rgba(125,211,252,0.85)', textDecoration: 'underline' }}
            >
              Konrad Kwiatkowski
            </Link>
          </span>
          {isPl ? ', założyciel HussarAi' : ', Founder of HussarAi'}
        </p>
        <p style={styles.lede} data-testid="wiedza-article-lede">
          {isPl ? ledePl : ledeEn}
        </p>

        <div data-testid="wiedza-article-body">
          {(isPl ? bodyPl : bodyEn).map((block, i) => {
            if (block.type === 'h2') return <h2 key={i} style={styles.h2}>{block.text}</h2>;
            if (block.type === 'callout') return <div key={i} style={styles.callout}>{block.text}</div>;
            if (block.type === 'p') return <p key={i} style={styles.p}>{block.text}</p>;
            return null;
          })}
        </div>

        <div style={styles.doctrine} data-testid="wiedza-article-doctrine">
          {isPl
            ? 'AI przygotowuje. Ludzie decydują. Każde działanie zostawia ślad audytowy.'
            : 'AI prepares. Humans decide. Every action leaves an audit trail.'}
        </div>

        {internalLinks && internalLinks.length > 0 && (
          <div style={styles.linksRow} data-testid="wiedza-article-internal-links">
            {internalLinks.map((l) => (
              <Link key={l.to} to={l.to} style={styles.linkChip}>
                {isPl ? l.labelPl : l.labelEn}
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default WiedzaArticleLayout;
